import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from 'aws-amplify/api';
import { addHours, isAfter } from 'date-fns';
import generateUniqueId from 'generate-unique-id';
import _ from 'lodash';
import { calenderTheme } from 'themes/calenderTheme';

import {
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	Menu,
	MenuItem,
	TextField,
	ThemeProvider,
	Tooltip,
	Typography,
} from '@mui/material';
import { DesktopDatePicker, DesktopTimePicker, MobileDatePicker } from '@mui/x-date-pickers';
import { AddRounded } from '@mui/icons-material';

import { AutoCompleteInput } from 'components/Common/AutoCompleteOption';
import { Center } from 'components/Common/Centered';
import { EventElementInputBox } from 'components/Event/EventElementInputBox';
import { EventInput } from 'components/Event/EventInput';

import * as EventService from 'services/eventService';
import * as SourceService from 'services/sourceService';

import { useToaster } from 'contexts/ToasterContext';

import {
	DATE_FORMAT,
	OutputTypeDisplayName,
	SourceTypeDisplayName,
	SRT_LISTENER_PORT_RANGE,
} from 'constants/CommonConstants';
import { OutputConfigDetail, OutputsCount } from 'constants/EventConstants/OutputConstants';
import { SourceConfigDetail, SourceCount } from 'constants/EventConstants/SourceConstants';

import {
	AWSResourceType,
	EventType,
	GroupInfo,
	OutputType,
	PopUpButtonProps,
	SingleAPIEvent,
	SourceType,
	UpdateEventPayload,
} from 'types';
import {
	areAllObjectValuesValid,
	checkObjectNotEmpty,
	eventsTypeOptions,
	validateEventOutputSpecification,
	validateEventSourceSpecification,
} from 'utils/eventHelpers';
import { cidrRegex, getAPIErrorMessage, ipV4Regex, isValidDNS, timeZoneAbbreviated, uuid } from 'utils/helpers';

import { PopUp } from '.';
import { AdminGroupNames } from '../../constants/TeamConstants';
import { GroupInfoContext } from '../../contexts/GroupInfoContext';

interface EventPopUpProps {
	isEditMode?: boolean;
	isPopUpOpen: boolean;
	eventToEdit?: string;
	close: () => void | Promise<void>;
}

const EventSteps = [{ name: 'Event Details' }, { name: 'Configure Sources' }, { name: 'Add Outputs' }];

export function EventPopUp({ isPopUpOpen, close, isEditMode, eventToEdit }: EventPopUpProps): JSX.Element {
	const queryClient = useQueryClient();
	const { triggerToast } = useToaster();
	const { groups, isUserAdminGroup, currentUserGroup } = useContext(GroupInfoContext);
	const isUserAdmin = isUserAdminGroup();
	const [selectedGroup, setselectedGroup] = useState<GroupInfo>(null);
	const [selectedEventType, setSelectedEventType] = useState<EventType>(null);
	const [selectedSourceArray, setSelectedSourceArray] = useState([]);
	const [selectedOutputArray, setSelectedOutputArray] = useState([]);
	const [currentStep, setCurrentStep] = useState(0);
	const [addSourcesMenuAnchorEl, setAddSourcesMenuAnchorEl] = useState(null);
	const isAddSourcesMenuOpen = Boolean(addSourcesMenuAnchorEl);
	const [addOutputsMenuAnchorEl, setAddOutputsMenuAnchorEl] = useState(null);
	const isAddOutputsMenuOpen = Boolean(addOutputsMenuAnchorEl);
	const [inputEventName, setInputEventName] = useState('');
	const [inputEventDate, setInputEventDate] = useState(addHours(new Date(), 1));
	const [selectedSources, setSelectedSources] = useState<{ [K in string]: any }>({});
	const [selectedOutputs, setSelectedOutputs] = useState<{ [K in string]: any }>({});
	const [defaultSource, setDefaultSource] = useState('');
	const nonAdminGroups = groups.filter((group) => !Object.keys(AdminGroupNames).includes(group.name));
	const timeZone = timeZoneAbbreviated();
	const isMounted = useRef(false);

	const getUnSelectedSources = () => {
		const currSelectedSources = Object.keys(selectedSources);
		const multipleSources = _.compact(
			Object.keys(SourceConfigDetail).map((source) => {
				const value = SourceConfigDetail[source as SourceType];
				return value.isMultiple ? source : undefined;
			}),
		) as SourceType[];

		const unselectedSourcesFinal: SourceType[] = [];
		Object.keys(SourceType).forEach((source) => {
			if (!currSelectedSources.includes(source) && source !== SourceType.ZIXI_MULTI) {
				unselectedSourcesFinal.push(source as SourceType);
			}
		});

		multipleSources.forEach((sourceType) => {
			if (!unselectedSourcesFinal.includes(sourceType)) {
				unselectedSourcesFinal.push(sourceType);
			}
		});
		return unselectedSourcesFinal;
	};

	const getUnSelectedOutputs = () => {
		const currSelectedOutputs = Object.keys(selectedOutputs);
		const multipleOutputs = _.compact(
			Object.keys(OutputConfigDetail).map((output) => {
				const value = OutputConfigDetail[output as OutputType];
				return value.isMultiple ? output : undefined;
			}),
		) as OutputType[];

		const unselectedOutputsFinal: OutputType[] = [];
		Object.keys(OutputType).forEach((output) => {
			if (!currSelectedOutputs.includes(output) && output !== OutputType.COMMONCHANNEL) {
				unselectedOutputsFinal.push(output as OutputType);
			}
		});

		multipleOutputs.forEach((outputType) => {
			if (!unselectedOutputsFinal.includes(outputType)) {
				unselectedOutputsFinal.push(outputType);
			}
		});

		return unselectedOutputsFinal;
	};

	const handleOnUnSelectSource = (sourceType: SourceType, spec: any) => {
		setSelectedSourceArray(
			selectedSourceArray.filter((source) => {
				if (source.type === sourceType && _.isEqual(source.spec, spec)) {
					return false;
				} else {
					return true;
				}
			}),
		);
		spec?.index === defaultSource && setDefaultSource('');

		const sources = { ...selectedSources };
		const filteredSources = selectedSources[sourceType].filter((sourceSpecification) => {
			if (_.isEqual(sourceSpecification, spec)) {
				return false;
			} else {
				return true;
			}
		});
		if (filteredSources.length === 0) {
			delete sources[sourceType];
		} else {
			sources[sourceType] = filteredSources;
		}

		setSelectedSources(sources);
	};

	const handleOnUnSelectOutput = (outputType: OutputType, spec: any) => {
		setSelectedOutputArray(
			selectedOutputArray.filter((output) => {
				if (output.type === outputType && _.isEqual(output.spec, spec)) {
					return false;
				} else {
					return true;
				}
			}),
		);
		const outputs = { ...selectedOutputs };
		const filteredOutputs = selectedOutputs[outputType].filter((outputSpecification) => {
			if (_.isEqual(outputSpecification, spec)) {
				return false;
			} else {
				return true;
			}
		});

		if (filteredOutputs.length === 0) {
			delete outputs[outputType];
		} else {
			outputs[outputType] = filteredOutputs;
		}

		setSelectedOutputs(outputs);
	};

	/*
  ...............................................
  ...............................................
  Note: For both source and output, 'spec' represents the index of the source/output to maintain their order.
  It's an 8-digit random string provided by the uuid package.
  In the 'editEvent' mode, 'spec' serves as the id from the API for the source/output, acting as their index.
  ...............................................
  ...............................................
*/

	const handleSelectSource = (sourceType: SourceType) => {
		const addSourceItem = (type: string, item?: any) => {
			const array = selectedSources[type] || [];
			array.push(item);
			setSelectedSourceArray([
				...selectedSourceArray,
				{
					type,
					spec: item,
				},
			]);
			setSelectedSources({
				...selectedSources,
				[type]: array,
			});
		};
		switch (sourceType) {
			case SourceType.ELEMENTAL_LINK:
				addSourceItem(sourceType, { deviceId: '', index: `${uuid(8)}_dummy` });
				break;

			case SourceType.SLATE_INPUT:
				addSourceItem(sourceType, { slateId: '', index: `${uuid(8)}_dummy` });
				break;

			case SourceType.SRT:
				addSourceItem(sourceType, {
					port: _.random(SRT_LISTENER_PORT_RANGE.FROM, SRT_LISTENER_PORT_RANGE.TO).toString(),
					streamId: '',
					password: '',
					maxBitrate: '150',
					host: '',
					mode: 'Listener',
					index: `${uuid(8)}_dummy`,
				});
				break;
			case SourceType.RTMP:
				addSourceItem(sourceType, { index: `${uuid(8)}_dummy` });
				break;
			case SourceType.ZIXI: {
				addSourceItem(sourceType, {
					index: `${uuid(8)}_dummy`,
					mode: 'Push',
					remotePort: '2077',
					remoteHost: '',
					streamId: `zixi-Pull-source-${uuid(6)}`, // will be used only on Zixi Pull mode
				});
				break;
			}
		}
	};

	const handleSelectOutput = (outputType: OutputType) => {
		const addOutputItem = (type: string, item: any) => {
			const array = selectedOutputs[type] || [];
			array.push(item);
			setSelectedOutputArray([
				...selectedOutputArray,
				{
					type,
					spec: item,
				},
			]);

			setSelectedOutputs({
				...selectedOutputs,
				[type]: array,
			});
		};

		switch (outputType) {
			case OutputType.HLS:
				addOutputItem(outputType, { tag: 'NHL.com', index: `${uuid(8)}_dummy` });
				break;

			case OutputType.SRT:
				addOutputItem(outputType, {
					host: '',
					port: _.random(SRT_LISTENER_PORT_RANGE.FROM, SRT_LISTENER_PORT_RANGE.TO).toString(),
					password: '',
					tag: '',
					cidr: '0.0.0.0/0',
					streamId: '',
					mode: 'Listener',
					index: `${uuid(8)}_dummy`,
				});
				break;
			case OutputType.ZIXI:
				addOutputItem(outputType, {
					mode: 'Pull',
					target: '',
					streamId: `zixi-Push-target-${uuid(6)}`, // will be used only on Zixi Push mode
					tag: '',
					index: `${uuid(8)}_dummy`,
				});
				break;
			case OutputType.RTMP:
				addOutputItem(outputType, { url: '', streamId: '', tag: '', index: `${uuid(8)}_dummy` });
				break;
		}
	};

	const handleSourceSpecificationsChange = <T extends SourceType>(type: T, key: string, value: any, spec: any) => {
		let trimmedValue = typeof value === 'string' ? value?.trim() : value;
		if (['port', 'remotePort'].includes(key) && value?.toString()?.includes('.')) {
			trimmedValue = value?.split('.')[0];
		}
		setSelectedSources((sources) => {
			const index = _.findIndex(sources[type], ['index', spec.index]);
			if (index === -1) {
				return sources;
			}
			const updatedSource = { ...sources[type][index], [key]: trimmedValue };

			sources[type][index] = updatedSource;
			return { ...sources };
		});
		setSelectedSourceArray(
			selectedSourceArray.map((source) => {
				if (source.type === type && _.isEqual(source.spec, spec)) {
					const updatedSpec = { ...source.spec, [key]: trimmedValue };

					return { ...source, spec: updatedSpec };
				}
				return source;
			}),
		);
	};

	const handleOutputSpecificationsChange = <T extends OutputType>(type: T, key: string, value: any, spec: any) => {
		let trimmedValue = typeof value === 'string' ? value?.trim() : value;
		if (['port', 'remotePort'].includes(key) && value?.toString()?.includes('.')) {
			trimmedValue = value?.split('.')[0];
		}
		setSelectedOutputs((outputs) => {
			const index = _.findIndex(outputs[type], ['index', spec.index]);
			if (index === -1) {
				return outputs;
			}

			const updatedOutput = { ...outputs[type][index], [key]: trimmedValue };
			outputs[type][index] = updatedOutput;

			return { ...outputs };
		});
		setSelectedOutputArray(
			selectedOutputArray.map((output) => {
				if (output.type === type && _.isEqual(output.spec, spec)) {
					const updatedSpec = { ...output.spec, [key]: trimmedValue };
					return { ...output, spec: updatedSpec };
				} else {
					return output;
				}
			}),
		);
	};

	const { mutate: createEvent, isPending: isCreateLoading } = useMutation({
		mutationFn: EventService.createEvent,
		onSuccess: async () => {
			triggerToast({ type: 'success', message: 'Created event!' });
			queryClient.invalidateQueries({ queryKey: ['events'] });
			setInputEventName('');
			setInputEventDate(addHours(new Date(), 1));
			setSelectedEventType(null);
			setDefaultSource('');
			close();
		},
		onError() {
			triggerToast({ type: 'error', message: 'Unable to create event!' });
		},
	});

	const { mutate: updateEvent, isPending: isUpdateLoading } = useMutation({
		mutationFn: EventService.updateEvent,
		onSuccess() {
			triggerToast({ message: 'Updated event!', type: 'success' });
			queryClient.invalidateQueries({ queryKey: ['events'] });
			queryClient.invalidateQueries({ queryKey: ['event', event.id] });
			setInputEventName(event?.name);
			setInputEventDate(new Date(event.scheduledTime));
			setSelectedEventType(null);
			close();
		},
		onError(error: ApiError) {
			const defaultMessage = 'Failed to update event!';
			const errMessage = getAPIErrorMessage(error) || defaultMessage;
			const statusCode = error?.response?.statusCode || 500;
			triggerToast({
				message: statusCode === 500 ? defaultMessage : errMessage,
				type: 'error',
			});
		},
	});

	const {
		data: awsResources,
		isFetching: isFetchingAWSResources,
		refetch: refetchAWSResources,
	} = useQuery({
		queryKey: ['awsResources'],
		queryFn: () => SourceService.listAWSResources(AWSResourceType.MEDIALIVE_DEVICE),
		enabled: false,
	});

	const {
		data: slates,
		isFetching: isFetchingSlates,
		refetch: refetchSlates,
	} = useQuery({
		queryKey: ['slates'],
		queryFn: () => SourceService.listSlates(currentUserGroup?.id),
		enabled: false,
	});

	const isDateValid = useMemo(() => {
		const isValid = isAfter(new Date(inputEventDate), new Date());
		return isValid;
	}, [inputEventDate]);

	const isFormValid = () => {
		switch (currentStep) {
			case 0:
				const isEventNameValid = inputEventName?.length > 0;
				const isGroupValid = isUserAdmin && !isEditMode ? Boolean(selectedGroup?.id) : true;
				const isEventTypeValid = isUserAdmin ? Boolean(selectedEventType?.value) : true;
				return isDateValid && isEventNameValid && isGroupValid && isEventTypeValid;

			case 1:
				const filteredSources = _.omit(selectedSources, [SourceType.ZIXI_MULTI]);
				const validSources = Boolean(Object.keys(filteredSources).length > 0);
				if (validSources) {
					const filteredData = _.omit(filteredSources, [SourceType.RTMP]);
					if (filteredData[SourceType.SRT]) {
						for (const source of filteredData[SourceType.SRT]) {
							if (source?.password && source?.password.length < 10) {
								return false;
							}
							if (
								source.mode === 'Listener' &&
								source?.port &&
								!(source?.port >= SRT_LISTENER_PORT_RANGE.FROM && source?.port <= SRT_LISTENER_PORT_RANGE.TO)
							) {
								return false;
							}
							if (source.mode === 'Caller' && source?.port && !(source?.port >= 0 && source?.port <= 65535)) {
								return false;
							}
							if (
								source.mode === 'Caller' &&
								source?.host &&
								!ipV4Regex.test(source?.host) &&
								!isValidDNS(source?.host)
							) {
								return false;
							}
						}
					}
					if (filteredData[SourceType.ZIXI]) {
						for (const source of filteredData[SourceType.ZIXI]) {
							if (
								source.mode === 'Pull' &&
								source?.remotePort &&
								!(source?.remotePort >= 0 && source?.remotePort <= 65535)
							) {
								return false;
							}
							if (source.mode === 'Pull' && !source?.streamId) {
								return false;
							}
							if (
								source.mode === 'Pull' &&
								source?.remoteHost &&
								!ipV4Regex.test(source?.remoteHost) &&
								!isValidDNS(source?.remoteHost)
							) {
								return false;
							}
						}
					}
					const modifiedResources = _.mapValues(filteredData, (value) => {
						return value.map((source) => {
							if (source.mode === 'Listener') {
								const { host, password, streamId, ...rest } = source;
								return rest;
							} else if (source.mode === 'Caller') {
								const { streamId, password, ...rest } = source;
								return rest;
							} else if (source.mode == 'Push') {
								const { remotePort, remoteHost, streamId, ...rest } = source;
								return rest;
							} else if (source.mode == 'Pull') {
								const { streamId, ...rest } = source;
								return rest;
							} else {
								return source;
							}
						});
					});
					return checkObjectNotEmpty(modifiedResources);
				} else {
					return false;
				}
			case 2:
				const filteredOutputs = _.omit(selectedOutputs, [OutputType.COMMONCHANNEL]);
				const validOutputs = Boolean(Object.keys(filteredOutputs).length > 0);
				if (validOutputs) {
					if (selectedOutputs[OutputType.RTMP]) {
						for (const output of selectedOutputs[OutputType.RTMP]) {
							if (!output?.url?.startsWith('rtmp://') && !output?.url?.startsWith('rtmps://')) {
								return false;
							} else {
								if (output?.url?.split('://')[1] === '') {
									return false;
								}
							}
						}
					}
					const filteredData = _.omit(filteredOutputs, [OutputType.HLS, OutputType.RTMP]);
					if (filteredData[OutputType.SRT]) {
						for (const output of filteredData[OutputType.SRT]) {
							if (output?.password && output?.password.length < 10) {
								return false;
							}
							if (
								output.mode === 'Listener' &&
								output?.port &&
								!(output?.port >= SRT_LISTENER_PORT_RANGE.FROM && output?.port <= SRT_LISTENER_PORT_RANGE.TO)
							) {
								return false;
							}
							if (output.mode === 'Listener' && output?.cidr && !cidrRegex.test(output?.cidr)) {
								return false;
							}
							if (output.mode === 'Caller' && output?.port && !(output?.port >= 0 && output?.port <= 65535)) {
								return false;
							}
							if (checkDuplicateSpecification(SourceType.SRT, output)) {
								return false;
							}
							if (
								output.mode === 'Caller' &&
								output?.host &&
								!ipV4Regex.test(output?.host) &&
								!isValidDNS(output?.host)
							) {
								return false;
							}
						}
					}

					if (filteredData[OutputType.ZIXI]) {
						for (const output of filteredData[OutputType.ZIXI]) {
							if (
								output.mode === 'Push' &&
								output?.target &&
								!ipV4Regex.test(output?.target) &&
								!isValidDNS(output?.target)
							) {
								return false;
							}
							if (output.mode === 'Push' && !output?.streamId) {
								return false;
							}
							if (checkDuplicateSpecification(SourceType.ZIXI, output)) {
								return false;
							}
						}
					}
					const filteredSelectedOutput = _.pickBy(filteredOutputs, (val) => !_.isEmpty(val));
					const modifiedResources = _.mapValues(filteredSelectedOutput, (value) => {
						return value.map((output) => {
							if (output.mode === 'Listener') {
								const { streamId, cidr, password, host, ...rest } = output;
								return rest;
							} else if (output.mode === 'Caller') {
								const { cidr, streamId, password, ...rest } = output;
								return rest;
							} else if (output.mode === 'Pull') {
								const { streamId, target, ...rest } = output;
								return rest;
							} else if (output.mode == 'Push') {
								const { streamId, ...rest } = output;
								return rest;
							} else {
								return output;
							}
						});
					});

					if (selectedOutputs[OutputType.SRT]) {
						for (const output of selectedOutputs[OutputType.SRT]) {
							const cidrRegex = /^(?:\d{1,3}\.){3}\d{1,3}\/(?:[0-9]|[1-2][0-9]|3[0-2])$/;
							if (output?.cidr && !cidrRegex.test(output?.cidr)) {
								return false;
							}
							if (output?.password && output?.password.length < 10) {
								return false;
							}
						}
					}
					return checkObjectNotEmpty(modifiedResources);
				} else {
					return false;
				}
			default: {
				return true;
			}
		}
	};

	const handleCreateEvent = () => {
		const eventId = generateUniqueId({
			length: 8,
		}).toUpperCase();
		const group = isUserAdmin ? selectedGroup.id : currentUserGroup.id;
		let selectedDefaultSource = '';
		createEvent({
			id: eventId,
			name: inputEventName,
			scheduledTime: inputEventDate.toISOString(),
			group,
			tag: selectedEventType.value,
			type: 'EVENT',
			sources: _.compact(
				Object.keys(selectedSources)
					.map((sourceType) => {
						const sourceSpecifications = selectedSources[sourceType];
						return sourceSpecifications.map((sourceSpecification) => {
							const processedSource = validateEventSourceSpecification(sourceSpecification);
							const sourceUniqueId = uuid();
							if (sourceSpecification.index === defaultSource) {
								selectedDefaultSource = sourceUniqueId;
							}
							return (
								areAllObjectValuesValid(processedSource) && {
									id: sourceUniqueId,
									eventId,
									type: sourceType,
									group,
									specifications: [
										SourceType.SLATE_INPUT,
										SourceType.ELEMENTAL_LINK,
										SourceType.SRT,
										SourceType.ZIXI,
									].includes(sourceType as SourceType)
										? (processedSource as Record<string, string>)
										: {},
									resources: [] as [],
								}
							);
						});
					})
					.flat(),
			),
			outputs: _.compact(
				Object.keys(selectedOutputs).map((outputType) => {
					const outputSpecifications = selectedOutputs[outputType];
					return outputSpecifications.map((outputSpecification) => {
						if (
							Object.keys(outputSpecification).includes('url') &&
							!outputSpecification.url?.startsWith('rtmps://') &&
							!outputSpecification.url.startsWith('rtmp://')
						) {
							outputSpecification.url = 'rtmp://' + outputSpecification.url;
						}
						const processedOutput = validateEventOutputSpecification(outputSpecification);
						return (
							areAllObjectValuesValid(processedOutput) && {
								id: uuid(),
								eventId,
								type: outputType,
								resources: [],
								specifications: processedOutput,
								group,
								...(processedOutput.tag && { tag: processedOutput.tag }),
							}
						);
					});
				}),
			).flat(),
			defaultSourceId: selectedDefaultSource,
		});
	};

	const handleUpdateEvent = () => {
		let selectedDefaultSource = '';
		const updatedEvent: UpdateEventPayload = {
			...event,
			name: inputEventName,
			scheduledTime: inputEventDate.toISOString(),
			tag: selectedEventType?.value,
			sources: _.compact(
				Object.keys(selectedSources).map((sourceType) => {
					const sourceSpecifications = selectedSources[sourceType];
					return sourceSpecifications.map((sourceSpecification) => {
						const existingSource = _.find(event.sources, ['id', sourceSpecification.index]);
						const processedSource = validateEventSourceSpecification(sourceSpecification);
						const sourceUniqueId = existingSource ? existingSource?.id : uuid();
						if (sourceSpecification.index === defaultSource) {
							selectedDefaultSource = sourceUniqueId;
						}
						return (
							areAllObjectValuesValid(processedSource) && {
								id: sourceUniqueId,
								eventId: eventToEdit,
								type: sourceType,
								group: event.group,
								specifications: [
									SourceType.SLATE_INPUT,
									SourceType.ELEMENTAL_LINK,
									SourceType.SRT,
									SourceType.ZIXI,
								].includes(sourceType as SourceType)
									? (processedSource as Record<string, string>)
									: {},
								resources: existingSource ? existingSource['resources'] : [],
							}
						);
					});
				}),
			).flat(),
			outputs: _.compact(
				Object.keys(selectedOutputs).map((outputType) => {
					if (
						outputType === OutputType.COMMONCHANNEL &&
						!Object.keys(selectedOutputs).some(
							(outputType) => outputType === OutputType.RTMP || outputType === OutputType.HLS,
						)
					) {
						return;
					} //Note: If there is no HLS or RTMP output, then remove the Common Channel output

					const existingChannelOutput = _.find(event.outputs, ['type', OutputType.COMMONCHANNEL]);
					const outputSpecifications = selectedOutputs[outputType];
					return outputSpecifications.map((outputSpecification) => {
						const existingOutput = _.find(event.outputs, ['id', outputSpecification.index]);
						if (
							Object.keys(outputSpecification).includes('url') &&
							!outputSpecification.url?.startsWith('rtmps://') &&
							!outputSpecification.url.startsWith('rtmp://')
						) {
							outputSpecification.url = 'rtmp://' + outputSpecification.url;
						}
						const processedOutput = validateEventOutputSpecification(outputSpecification);
						const outputId = (() => {
							if (outputType === OutputType.COMMONCHANNEL && existingChannelOutput?.id) {
								return existingChannelOutput.id;
							}
							return existingOutput ? existingOutput.id : uuid();
						})();
						const resourcesList = (() => {
							if (outputType === OutputType.COMMONCHANNEL && existingChannelOutput?.resources) {
								return existingChannelOutput.resources;
							}
							return existingOutput ? existingOutput.resources : [];
						})();

						return (
							//Note: If there is no HLS or RTMP output, then remove the Common Channel output
							areAllObjectValuesValid(processedOutput) && {
								id: outputId,
								eventId: eventToEdit,
								type: outputType,
								group: event.group,
								specifications: processedOutput,
								resources: resourcesList,
								tag: outputSpecification.tag,
								/*
								 * The following code maintains the status of the Common Channel output.
								 * If event.outputs does not contain the channel output (i.e., no HLS or RTMP output) from the API,
								 * it implies that there's no common channel. Therefore, there's no need to create a status.
								 * Add status only if event.outputs includes the channel output.
								 */
								...(((outputType === OutputType.HLS || outputType === OutputType.RTMP) &&
									Object.keys(selectedOutputs).includes(OutputType.COMMONCHANNEL)) ||
								outputType === OutputType.COMMONCHANNEL
									? {
											status: existingChannelOutput?.status,
										}
									: {}),
							}
						);
					});
				}),
			).flat(),
			defaultSourceId: selectedDefaultSource,
		};
		delete updatedEvent['stream'];
		delete updatedEvent['createdBy'];
		delete updatedEvent['broadcasterId'];
		updateEvent(updatedEvent);
	};

	const buttons: PopUpButtonProps[] = [
		currentStep !== 0
			? {
					name: 'BACK',
					handler: async () => {
						if (currentStep === 2) {
							await refetchAWSResources();
							await refetchSlates();
						}
						setCurrentStep((step) => step - 1);
					},
					variant: 'outlined',
					isLoading: isFetchingAWSResources || isFetchingSlates,
				}
			: { name: 'CANCEL', handler: close, variant: 'outlined' },
		currentStep === EventSteps.length - 1
			? {
					name: isEditMode ? 'UPDATE' : 'CREATE',
					handler: isEditMode ? handleUpdateEvent : handleCreateEvent,
					variant: 'contained',
					isLoading: isCreateLoading || isUpdateLoading,
				}
			: {
					name: 'NEXT',
					handler: async () => {
						if (currentStep === 0) {
							isMounted.current = true;
							await Promise.all([refetchAWSResources(), refetchSlates()]);
						}
						if (isMounted.current && isPopUpOpen) {
							setCurrentStep((step) => step + 1);
						}
					},
					variant: 'contained',
					isLoading: isMounted.current && isPopUpOpen ? isFetchingAWSResources || isFetchingSlates : false,
				},
	];

	const handleEventNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setInputEventName(event.target.value);
	};

	const handleEventDateChange = (event: Date | null) => {
		setInputEventDate(event);
	};

	const renderSourcesMenu = () => {
		const unSelectedSources = getUnSelectedSources();
		return (
			<Menu
				anchorEl={addSourcesMenuAnchorEl}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				PaperProps={{
					style: {
						width: 190,
					},
				}}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				open={isAddSourcesMenuOpen}
				onClose={() => setAddSourcesMenuAnchorEl(null)}
				onClick={() => setAddSourcesMenuAnchorEl(null)}
			>
				{unSelectedSources.sort().map((sourceType) => {
					return (
						<MenuItem key={sourceType} onClick={() => handleSelectSource(sourceType)}>
							{SourceTypeDisplayName[sourceType]}
						</MenuItem>
					);
				})}
			</Menu>
		);
	};

	const renderOutputsMenu = () => {
		const unselectedOutputs = getUnSelectedOutputs();
		return (
			<Menu
				anchorEl={addOutputsMenuAnchorEl}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				PaperProps={{
					style: {
						width: 190,
					},
				}}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				open={isAddOutputsMenuOpen}
				onClose={() => setAddOutputsMenuAnchorEl(null)}
				onClick={() => setAddOutputsMenuAnchorEl(null)}
			>
				{unselectedOutputs.sort().map((outputType) => {
					return (
						<MenuItem key={outputType} onClick={() => handleSelectOutput(outputType)}>
							{OutputTypeDisplayName[outputType]}
						</MenuItem>
					);
				})}
			</Menu>
		);
	};

	const renderSourceInputField = (
		sourceType: string,
		selectedSourceSpecification: any,
		isDefaultSource: boolean,
		spec?: any,
	) => {
		return (
			<EventElementInputBox
				key={spec.index}
				name={SourceTypeDisplayName[sourceType]}
				deleteTooltip="delete source"
				showDelete
				onDelete={() => handleOnUnSelectSource(sourceType as SourceType, spec)}
				isDefaultSource={isDefaultSource}
				isSourceBox={true}
				setAsDefaultSource={() => setDefaultSource(spec.index)}
			>
				<EventInput
					type={sourceType as SourceType}
					spec={spec}
					details={selectedSourceSpecification}
					inputType={'SOURCE'}
					onChange={handleSourceSpecificationsChange}
					options={{
						awsResources: awsResources.filter((resource) =>
							Boolean(resource.details?.Name)
								? !isUserAdmin
									? resource.group === currentUserGroup?.id
									: (selectedGroup?.id || event?.group) === resource.group
								: false,
						),
						slates: slates.filter((resource) =>
							!isUserAdmin
								? resource.group === currentUserGroup?.id
								: (selectedGroup?.id || event?.group) === resource.group,
						),
					}}
					selectedSources={selectedSources}
					onClickRefreshSlates={refetchSlates}
					isFetchingSlates={isFetchingSlates}
					group={selectedGroup?.id}
				/>
			</EventElementInputBox>
		);
	};

	const checkDuplicateSpecification = (type: string, spec: string) => {
		const index = spec?.['index'];
		const findIndex = selectedOutputs[type]?.findIndex((output) => output?.['index'] === index);
		if (type === OutputType.SRT) {
			const port = spec?.['port'];
			const filteredSRT = selectedOutputs[type].filter(
				(output, idx) => output?.['index'] !== index && idx < findIndex && output?.mode === spec?.['mode'],
			);
			const filteredSRTSources =
				(selectedSources[type] && selectedSources[type].filter((source) => source?.mode === spec?.['mode'])) || [];
			if (spec?.['mode'] === 'Listener') {
				const isPortUnique = [...filteredSRT, ...filteredSRTSources].every((srt) => srt?.['port'] !== port);
				return !isPortUnique;
			}

			if (spec?.['mode'] === 'Caller' && spec?.['host']) {
				const host = spec?.['host'];

				const isHostUnique = [...filteredSRT, ...filteredSRTSources].every((output) => {
					return output?.['host'] === host ? output?.['port'] !== port : true;
				});
				return !isHostUnique;
			}
		}
		if (type === OutputType.RTMP && spec?.['url']) {
			const url = spec?.['url'];
			const filteredRTMP = selectedOutputs[type].filter(
				(output, idx) => output?.['index'] !== index && idx < findIndex,
			);
			const isURlUnique = filteredRTMP.every((output) => output?.['url'] !== url);
			return !isURlUnique;
		}

		if (type === OutputType.ZIXI) {
			const target = spec?.['target'];
			const streamId = spec?.['streamId'];
			if (target) {
				const filteredZIXIPush = selectedOutputs[type].filter(
					(output, idx) => output?.['index'] !== index && idx < findIndex && output?.mode === spec?.['mode'],
				);
				const istargetUnique = filteredZIXIPush.some(
					(output) => output?.['target'] == target && output?.['streamId'] == streamId,
				);
				return istargetUnique;
			}
		}
		return false;
	};

	const renderLabelName = (outputType, spec) => {
		if (checkDuplicateSpecification(outputType, spec)) {
			if (outputType === OutputType.SRT && spec.mode === 'Listener') {
				return 'Duplicate SRT Listener (port must be unique)';
			}
			if (outputType === OutputType.SRT && spec.mode === 'Caller') {
				return 'Duplicate SRT Caller (host and port must be unique)';
			}
			if (outputType === OutputType.RTMP) {
				return 'Duplicate RTMP (url must be unique)';
			}
			return 'Duplicate specification';
		}
		return OutputTypeDisplayName[outputType];
	};

	const renderOutputInputField = (outputType: string, selectedOutputSpecification: any, spec?: any) => {
		return (
			<Box
				style={{
					position: 'relative',
				}}
			>
				<EventElementInputBox
					key={outputType}
					showDelete
					name={renderLabelName(outputType, spec)}
					deleteTooltip="delete output"
					onDelete={() => handleOnUnSelectOutput(outputType as OutputType, spec)}
					error={checkDuplicateSpecification(outputType, spec)}
					isSourceBox={false}
				>
					<EventInput
						spec={spec}
						inputType={'OUTPUT'}
						type={outputType as OutputType}
						details={selectedOutputSpecification}
						onChange={handleOutputSpecificationsChange}
					/>
				</EventElementInputBox>
				<Box
					display="flex"
					justifyContent="flex-end"
					sx={{
						position: 'absolute',
						bottom: '0px',
						right: '50%',
					}}
				></Box>
			</Box>
		);
	};

	useEffect(() => {
		if (isPopUpOpen) {
			setCurrentStep(0);
			setSelectedSources({});
			setSelectedOutputs({});
			setSelectedSourceArray([]);
			setSelectedOutputArray([]);
			setInputEventName('');
			setSelectedEventType(null);
			isMounted.current = false;
			setInputEventDate(addHours(new Date(), 1));
			!isUserAdmin ? setselectedGroup(currentUserGroup) : setselectedGroup(null);
		}
	}, [isPopUpOpen]);

	const {
		refetch: refetchEvent,
		data: event,
		isFetching: isEventRefetching,
	} = useQuery({
		queryKey: ['eventEdit', eventToEdit],
    queryFn: async () => {
      const data = await EventService.getEvent(eventToEdit);
      processEventQueryData(data);
      return data;
    },
		enabled: false,
	});

	function processEventQueryData(responseData: SingleAPIEvent) {
		setInputEventName(responseData.name);
		setInputEventDate(new Date(responseData.scheduledTime));
		setSelectedEventType(
			responseData.tag
				? eventsTypeOptions.find((option) => option.label === responseData.tag)
					? eventsTypeOptions.find((option) => option.label === responseData.tag)
					: { label: responseData.tag, value: responseData.tag }
				: null,
		);
		setDefaultSource(responseData.defaultSourceId || '');

		const sourceMap = {};
		const sourceIndexArray = [];

		responseData.sources.map((source) => {
			if (!sourceMap[source.type]) {
				sourceMap[source.type] = [];
			}
			if (source.type === SourceType.SRT) {
				source.specifications = {
					host: '',
					...source.specifications,
				};
			}
			if (source.type === SourceType.ZIXI) {
				source.specifications = {
					remotePort: source.specifications?.remotePort || '2077',
					...source.specifications,
				};
			}

			sourceMap[source.type].push({ ...source.specifications, index: source.id });
			sourceIndexArray.push({
				type: source.type,
				spec: { ...source.specifications, index: source.id },
			});
		});
		setSelectedSourceArray(sourceIndexArray);
		const finalSourceMap = { ...selectedSources, ...sourceMap };
		setSelectedSources(finalSourceMap);

		const outputMap = {};
		const outputIndexMap = [];
		responseData.outputs.map((output) => {
			if (!outputMap[output.type]) {
				outputMap[output.type] = [];
			}
			if (output.type === OutputType.SRT) {
				output.specifications = {
					host: '',
					...output.specifications,
				};
			}
			outputMap[output.type].push({
				...output.specifications,
				...(output.type !== OutputType.COMMONCHANNEL && { tag: output.tag }),
				index: output.id,
			});
			outputIndexMap.push({
				type: output.type,
				spec: {
					...output.specifications,
					...(output.type !== OutputType.COMMONCHANNEL && { tag: output.tag }),
					index: output.id,
				},
			});
		});

		setSelectedOutputArray(outputIndexMap);
		const finalOutputMap = { ...selectedOutputs, ...outputMap };
		setSelectedOutputs(finalOutputMap);
	}

	useEffect(() => {
		if (isPopUpOpen && isEditMode) {
			refetchEvent();
			setCurrentStep(0);
			setSelectedEventType(null);
			isMounted.current = false;
		}
	}, [isPopUpOpen, isEditMode]);

	const calculateValuesLength = (obj: any, excludeKeys: any[]) => {
		const objWithoutExcludedKeys = _.omit(obj, excludeKeys);
		const objValues = _.values(objWithoutExcludedKeys);
		return _.flatten(objValues).length;
	};

	const selectedOutputsValuesLength = calculateValuesLength(selectedOutputs, [OutputType.COMMONCHANNEL]);
	const selectedSourcesValuesLength = calculateValuesLength(selectedSources, [SourceType.ZIXI_MULTI]);

	return (
		<PopUp
			disableCTA={isCreateLoading || isUpdateLoading || !isFormValid()}
			isPopUpOpen={isPopUpOpen}
			title={EventSteps[currentStep]?.name || ''}
			buttons={buttons}
			description={
				<Typography variant="body2" sx={{ color: 'grey.600' }}>
					step {currentStep + 1}/{EventSteps.length}
				</Typography>
			}
			close={close}
			maxWidth="sm"
			fullWidth
		>
			{isEventRefetching ? (
				<Center minHeight="300px">
					<CircularProgress />
				</Center>
			) : (
				<Box display="flex" flexDirection="column">
					{currentStep === 0 ? (
						<>
							<TextField
								disabled={isCreateLoading || isUpdateLoading}
								size="small"
								label="Event name"
								value={inputEventName}
								onChange={handleEventNameChange}
							/>
							<Typography variant="subtitle2" mt="20px" sx={{ fontWeight: 600, color: 'grey.600' }}>
								Schedule streaming
							</Typography>
							<Box display="flex" flexDirection="column" gap="20px" pt="15px">
								<ThemeProvider theme={calenderTheme}>
									<Box display={{ md: 'block', xs: 'none' }}>
										<Tooltip placement="bottom" title={DATE_FORMAT}>
											<DesktopDatePicker
												disabled={isCreateLoading || isUpdateLoading}
												label="Schedule date"
												format={DATE_FORMAT}
												value={inputEventDate}
												onChange={handleEventDateChange}
												slotProps={{
													textField: {
														size: 'small',
														fullWidth: true,
													},
												}}
											/>
										</Tooltip>
									</Box>
									<Box display={{ xs: 'block', md: 'none' }}>
										<MobileDatePicker
											disabled={isCreateLoading || isUpdateLoading}
											label="Schedule date"
											format={DATE_FORMAT}
											value={inputEventDate}
											onChange={handleEventDateChange}
											slotProps={{
												textField: {
													size: 'small',
													fullWidth: true,
												},
											}}
										/>
									</Box>
									<DesktopTimePicker
										disabled={isCreateLoading || isUpdateLoading}
										label="Time"
										value={inputEventDate}
										onChange={handleEventDateChange}
										format={`hh:mm aa '${timeZone}'`}
										timeSteps={{ hours: 1, minutes: 1, seconds: 5 }}
										slotProps={{
											textField: {
												size: 'small',
												fullWidth: true,
											},
										}}
									/>
									{!isEditMode && (
										<Autocomplete
											value={selectedGroup}
											size="small"
											disabled={!isUserAdmin}
											options={nonAdminGroups}
											onChange={(_, value: GroupInfo) => setselectedGroup(value)}
											getOptionLabel={(option: GroupInfo) => `${option.name} (${option.id})`}
											renderInput={(params) => <TextField {...params} label="Select Group" />}
										/>
									)}
									<AutoCompleteInput
										label={'Event Type'}
										freeSolo={false}
										options={eventsTypeOptions}
										onChange={setSelectedEventType}
										value={selectedEventType}
									/>
								</ThemeProvider>
							</Box>
							{!isDateValid && (
								<Typography variant="caption" sx={{ color: 'error.main', marginTop: '10px' }}>
									*Schedule time cannot be less than current time
								</Typography>
							)}
						</>
					) : currentStep === 1 ? (
						<>
							{selectedSourceArray
								.filter((selectedSource) => selectedSource.type !== SourceType.ZIXI_MULTI)
								.map((source, index) => {
									const selectedSource = selectedSources[source.type];
									const sourceSpecification = _.find(selectedSource, source.spec);
									if (index === 0 && !defaultSource) {
										setDefaultSource(source.spec.index);
									}
									return renderSourceInputField(
										source.type,
										sourceSpecification,
										defaultSource ? source.spec.index === defaultSource : index === 0,
										source.spec,
									);
								})}
							{selectedSourceArray.filter((selectedSource) => selectedSource.type !== SourceType.ZIXI_MULTI).length >
								1 && (
								<Box>
									<Typography variant="body1" fontSize={'12px'} color={'grey.600'} sx={{ marginBottom: '12px' }}>
										* Click on the radio button next to the source&apos;s title to make it as the default source. On
										every Stream START, the default source is enabled automatically.
									</Typography>
								</Box>
							)}
							{selectedSourceArray.filter((selectedSource) =>
								[SourceType.SRT, SourceType.ZIXI].includes(selectedSource.type),
							).length > 0 && (
								<Box>
									<Typography variant="body1" fontSize={'12px'} color={'grey.600'} sx={{ marginBottom: '12px' }}>
										* Any Leading / Trailing spaces will be removed.
									</Typography>
								</Box>
							)}
							<Box display="flex" justifyContent="flex-end">
								<Button
									size="small"
									variant="outlined"
									//remove this to enable adding multiple source
									disabled={getUnSelectedSources().length === 0 || selectedSourcesValuesLength === SourceCount}
									startIcon={<AddRounded />}
									onClick={(e) => setAddSourcesMenuAnchorEl(e.currentTarget)}
								>
									{selectedSourcesValuesLength > 0 ? 'Add Another Source' : 'Add Source'}
								</Button>
								{renderSourcesMenu()}
							</Box>
						</>
					) : currentStep === 2 ? (
						<>
							{selectedOutputArray
								.filter((selectedOutput) => selectedOutput.type !== OutputType.COMMONCHANNEL)
								.map((output) => {
									const selectedOutput = selectedOutputs[output.type];
									const outputSpecification = _.find(selectedOutput, output.spec);
									return renderOutputInputField(output?.type, outputSpecification, output.spec);
								})}
							{selectedOutputArray.filter((selectedOutput) =>
								[OutputType.SRT, OutputType.RTMP, OutputType.ZIXI].includes(selectedOutput.type),
							).length > 0 && (
								<Box>
									<Typography variant="body1" fontSize={'12px'} color={'grey.600'} sx={{ marginBottom: '12px' }}>
										* Any Leading / Trailing spaces will be removed.
									</Typography>
								</Box>
							)}
							<Box display="flex" justifyContent="flex-end">
								<Button
									size="small"
									variant="outlined"
									disabled={getUnSelectedOutputs().length === 0 || selectedOutputsValuesLength === OutputsCount}
									startIcon={<AddRounded />}
									onClick={(e) => setAddOutputsMenuAnchorEl(e.currentTarget)}
								>
									{selectedOutputsValuesLength > 0 ? 'Add Another Output' : 'Add Output'}
								</Button>
								{renderOutputsMenu()}
							</Box>
						</>
					) : (
						<></>
					)}
				</Box>
			)}
		</PopUp>
	);
}
